import { Link } from 'react-router-dom';
import { MapPin, Mail, Linkedin, Shield } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAdminStatus } from '@/hooks/useAdminStatus';
export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const {
    isSuperAdmin
  } = useAdminStatus();
  const fadeInUpItem = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
    return <footer className="bg-gradient-to-b from-white to-primary/5 text-gray-800 mt-8 sm:mt-12 pt-8 sm:pt-16 pb-6 sm:pb-8" aria-labelledby="footer-heading">
      <h2 id="footer-heading" className="sr-only">Footer</h2>
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 mb-8 sm:mb-12">
          {/* Company Info */}
          <motion.div className="space-y-3 sm:space-y-4" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} variants={fadeInUpItem}>
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-bold text-gray-800">The PEO App</h3>
            </div>
            <p className="text-sm text-gray-600">
              Simplifying the PEO shopping experience with our patent-pending platform.
            </p>
            <p className="text-sm text-gray-600 flex items-center">
              <MapPin className="h-4 w-4 mr-2 text-primary" />
              <span>Serving all 50 U.S. States</span>
            </p>
            <p className="text-sm text-gray-600 flex items-center">
              <Mail className="h-4 w-4 mr-2 text-primary" />
              <a href="mailto:info@thepeoapp.com" className="hover:text-primary transition-colors duration-200">info@thepeoapp.com</a>
            </p>
            <div className="mt-4 flex items-center">
              <a href="https://www.linkedin.com/company/the-peo-app/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="bg-primary/10 p-2 rounded-full hover:bg-primary/20 transition-colors duration-200">
                <Linkedin className="h-4 w-4 text-primary" />
              </a>
            </div>
          </motion.div>
          
          {/* Quick Links */}
          <motion.div className="space-y-3 sm:space-y-4" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} variants={fadeInUpItem}>
            <h3 className="text-base sm:text-lg font-semibold text-gray-800">Quick Links</h3>
            <nav aria-label="Footer quick links">
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-600">
                <li>
                  <Link to="/" className="hover:text-primary hover:underline transition-colors duration-200 flex items-center">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="hover:text-primary hover:underline transition-colors duration-200 flex items-center">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
                    About Us
                  </Link>
                </li>
                <li className="hidden sm:block">
                  <Link to="/partnerships" className="hover:text-primary hover:underline transition-colors duration-200 flex items-center">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
                    Partnerships
                  </Link>
                </li>
                <li className="hidden sm:block">
                  <Link to="/peo-vetting-process" className="hover:text-primary hover:underline transition-colors duration-200 flex items-center">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
                    PEO Vetting Process
                  </Link>
                </li>
                <li>
                  <a 
                    href="https://trust.delve.co/the-peo-app" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-primary hover:underline transition-colors duration-200 flex items-center"
                  >
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
                    Trust Center
                  </a>
                </li>
              </ul>
            </nav>
          </motion.div>
          
          {/* Services & Resources */}
          <motion.div className="space-y-3 sm:space-y-4" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} variants={fadeInUpItem}>
            <h3 className="text-base sm:text-lg font-semibold text-gray-800">PEO Services</h3>
            <nav aria-label="Footer PEO services">
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-600">
                <li>
                  <a href="/services/payroll" className="hover:text-primary hover:underline transition-colors duration-200 flex items-center">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
                    Payroll
                  </a>
                </li>
                <li>
                  <a href="/services/benefits" className="hover:text-primary hover:underline transition-colors duration-200 flex items-center">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
                    Benefits
                  </a>
                </li>
                <li>
                  <a href="/services/hr-compliance" className="hover:text-primary hover:underline transition-colors duration-200 flex items-center">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
                    HR Compliance
                  </a>
                </li>
                <li className="hidden sm:block">
                  <a href="/services/workers-comp" className="hover:text-primary hover:underline transition-colors duration-200 flex items-center">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
                    Workers' Comp
                  </a>
                </li>
                <li className="hidden sm:block">
                  <a href="/services/risk-management" className="hover:text-primary hover:underline transition-colors duration-200 flex items-center">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
                    Risk Management
                  </a>
                </li>
              </ul>
            </nav>
          </motion.div>
          
          {/* States Served */}
          <motion.div className="space-y-3 sm:space-y-4 col-span-2 md:col-span-1" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} variants={fadeInUpItem}>
            <h3 className="text-base sm:text-lg font-semibold text-gray-800">Top States</h3>
            <nav aria-label="Footer states served">
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-600">
                <li>
                  <a href="/states/california" className="hover:text-primary hover:underline transition-colors duration-200 flex items-center">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
                    California PEOs
                  </a>
                </li>
                <li>
                  <a href="/states/texas" className="hover:text-primary hover:underline transition-colors duration-200 flex items-center">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
                    Texas PEOs
                  </a>
                </li>
                <li>
                  <a href="/states/florida" className="hover:text-primary hover:underline transition-colors duration-200 flex items-center">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
                    Florida PEOs
                  </a>
                </li>
                <li>
                  <a href="/states/new-york" className="hover:text-primary hover:underline transition-colors duration-200 flex items-center">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
                    New York PEOs
                  </a>
                </li>
                <li>
                  <a href="/states" className="hover:text-primary hover:underline transition-colors duration-200 flex items-center">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
                    All 50 States
                  </a>
                </li>
              </ul>
            </nav>
          </motion.div>
        </div>
        
        {/* Legal */}
        <div className="border-t border-gray-200 pt-6">
          <div className="flex flex-col md:flex-row md:justify-between items-center">
            <p className="text-sm text-center md:text-left text-gray-600">
              <span className="font-semibold text-primary">Patent Pending Technology</span> - The PEO App, Inc. © {currentYear}
            </p>
            <nav aria-label="Footer legal navigation">
              <ul className="flex justify-center space-x-4 text-sm text-gray-500 mt-4 md:mt-0">
                {isSuperAdmin && <>
                    <li>
                      <Link to="/admin" className="hover:text-primary hover:underline transition-colors duration-200 opacity-50">Admin Portal</Link>
                    </li>
                    <li>
                      <span aria-hidden="true">•</span>
                    </li>
                  </>}
                <li>
                  <a href="/terms" className="hover:text-primary hover:underline transition-colors duration-200">Terms & Conditions</a>
                </li>
                <li>
                  <span aria-hidden="true">•</span>
                </li>
                <li>
                  <a href="/privacy" className="hover:text-primary hover:underline transition-colors duration-200">Privacy Policy</a>
                </li>
                <li>
                  <span aria-hidden="true">•</span>
                </li>
                <li>
                  <a href="/sitemap" className="hover:text-primary hover:underline transition-colors duration-200">Sitemap</a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
        
        {/* Disclaimer - Made smaller on mobile */}
        <div className="mt-4 sm:mt-8 pt-3 sm:pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 leading-normal sm:leading-relaxed max-w-full text-center md:text-left">
            <span className="font-medium">Disclaimer:</span> The PEO App, Inc. does not provide legal, tax, or accounting advice. Our materials are for general informational purposes only. Consult qualified professionals before making decisions.
          </p>
        </div>
      </div>
    </footer>;
};